import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, ZapIcon, FlameIcon, DropletIcon, WifiIcon, SmartphoneIcon, LandmarkIcon, ChevronRightIcon, ArrowLeftIcon, PlusIcon, FileTextIcon, ClockIcon, CheckCircle2Icon, XIcon, ShareIcon, CopyIcon, HomeIcon, CreditCardIcon, GraduationCapIcon, BuildingIcon, LoaderIcon, FingerprintIcon, ScanFaceIcon, LockIcon, RefreshCwIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 12
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: 'easeOut'
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06
    }
  }
};
type Step = 'home' | 'view_all_payees' | 'biller_list' | 'enter_consumer' | 'bill_details' | 'review' | 'auth' | 'processing' | 'success';
type AuthMethod = 'mpin' | 'thumb' | 'face';
interface SavedPayee {
  id: string;
  company: string;
  consumerName: string;
  consumerNumber: string;
  amount: number;
  daysLeft: number;
  category: string;
  iconBg: string;
  icon: React.ElementType;
  email: string;
}
interface Biller {
  id: string;
  name: string;
  email: string;
  category: string;
  iconBg: string;
}
const CATEGORIES = [{
  id: 'electricity',
  icon: ZapIcon,
  label: 'Electricity'
}, {
  id: 'gas',
  icon: FlameIcon,
  label: 'Gas'
}, {
  id: 'water',
  icon: DropletIcon,
  label: 'Water'
}, {
  id: 'internet',
  icon: WifiIcon,
  label: 'Internet'
}, {
  id: '1bill',
  icon: FileTextIcon,
  label: '1Bill'
}, {
  id: 'government',
  icon: LandmarkIcon,
  label: 'Government'
}, {
  id: 'education',
  icon: GraduationCapIcon,
  label: 'Education'
}, {
  id: 'credit',
  icon: CreditCardIcon,
  label: 'Credit Card'
}, {
  id: 'rental',
  icon: HomeIcon,
  label: 'Rental'
}];
const ALL_BILLERS: Biller[] = [{
  id: '1bill',
  name: '1BILL Invoice/Voucher',
  email: 'onebillinvoice@bopay',
  category: '1bill',
  iconBg: '#FBBF24'
}, {
  id: 'lesco',
  name: 'LESCO',
  email: 'lesco@bopay',
  category: 'electricity',
  iconBg: '#F37024'
}, {
  id: 'kelectric',
  name: 'K-Electric',
  email: 'kelectric@bopay',
  category: 'electricity',
  iconBg: '#0EA5E9'
}, {
  id: 'iesco',
  name: 'IESCO',
  email: 'iesco@bopay',
  category: 'electricity',
  iconBg: '#22C55E'
}, {
  id: 'sngpl',
  name: 'SNGPL',
  email: 'sngpl@bopay',
  category: 'gas',
  iconBg: '#EF4444'
}, {
  id: 'ssgc',
  name: 'SSGC',
  email: 'ssgc@bopay',
  category: 'gas',
  iconBg: '#F97316'
}, {
  id: 'kwsb',
  name: 'Karachi Water Board',
  email: 'kwsb@bopay',
  category: 'water',
  iconBg: '#0EA5E9'
}, {
  id: 'wasa',
  name: 'WASA Lahore',
  email: 'wasa@bopay',
  category: 'water',
  iconBg: '#06B6D4'
}, {
  id: 'stormfiber',
  name: 'StormFiber',
  email: 'stormfiber@bopay',
  category: 'internet',
  iconBg: '#1E3A8A'
}, {
  id: 'ptcl',
  name: 'PTCL',
  email: 'ptcl@bopay',
  category: 'internet',
  iconBg: '#0891B2'
}, {
  id: 'nayatel',
  name: 'Nayatel',
  email: 'nayatel@bopay',
  category: 'internet',
  iconBg: '#6366F1'
}, {
  id: 'jazz',
  name: 'Jazz',
  email: 'jazz@bopay',
  category: 'mobile',
  iconBg: '#DC2626'
}, {
  id: 'zong',
  name: 'Zong',
  email: 'zong@bopay',
  category: 'mobile',
  iconBg: '#16A34A'
}, {
  id: 'ufone',
  name: 'Ufone',
  email: 'ufone@bopay',
  category: 'mobile',
  iconBg: '#FBBF24'
}, {
  id: 'telenor',
  name: 'Telenor',
  email: 'telenor@bopay',
  category: 'mobile',
  iconBg: '#0EA5E9'
}, {
  id: 'fbr',
  name: 'FBR Income Tax',
  email: 'fbr@bopay',
  category: 'government',
  iconBg: '#15803D'
}, {
  id: 'punjab_excise',
  name: 'Punjab Excise & Tax',
  email: 'pet@bopay',
  category: 'government',
  iconBg: '#7C3AED'
}, {
  id: 'aiou',
  name: 'Allama Iqbal Open University',
  email: 'aiou@bopay',
  category: 'education',
  iconBg: '#1E40AF'
}, {
  id: 'lums',
  name: 'LUMS',
  email: 'lums@bopay',
  category: 'education',
  iconBg: '#9F1239'
}];
const INITIAL_PAYEES: SavedPayee[] = [{
  id: 'p1',
  company: 'StormFiber',
  consumerName: 'Muhammad Sohail Shawani',
  consumerNumber: '03223556978',
  amount: 3448,
  daysLeft: 10,
  category: 'internet',
  iconBg: '#1E3A8A',
  icon: WifiIcon,
  email: 'stormfiber@bopay'
}, {
  id: 'p2',
  company: 'LESCO',
  consumerName: 'Ali Hassan',
  consumerNumber: '034982349823',
  amount: 12450,
  daysLeft: 7,
  category: 'electricity',
  iconBg: '#F37024',
  icon: ZapIcon,
  email: 'lesco@bopay'
}, {
  id: 'p3',
  company: 'SNGPL',
  consumerName: 'Sara Ahmed',
  consumerNumber: '9823498234',
  amount: 3200,
  daysLeft: 2,
  category: 'gas',
  iconBg: '#EF4444',
  icon: FlameIcon,
  email: 'sngpl@bopay'
}];
const ACCOUNTS = [{
  id: 'acc1',
  label: 'Current Account',
  number: '****2656',
  balance: 'Rs 100,000.00'
}, {
  id: 'acc2',
  label: 'Savings Account',
  number: '****8932',
  balance: 'Rs 50,250.00'
}];
const TRANSACTION_FEE = 25;
const OVERDUE_SURCHARGE_PER_DAY = 50;
interface PaymentsScreenProps {
  onBack?: () => void;
  onNavigateHome?: () => void;
}
export function PaymentsScreen({
  onBack,
  onNavigateHome
}: PaymentsScreenProps = {}) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const [step, setStep] = useState<Step>('home');
  // State
  const [savedPayees, setSavedPayees] = useState<SavedPayee[]>(INITIAL_PAYEES);
  const [homeSearch, setHomeSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('electricity');
  const [billerSearch, setBillerSearch] = useState('');
  const [selectedBiller, setSelectedBiller] = useState<Biller | null>(null);
  const [selectedPayee, setSelectedPayee] = useState<SavedPayee | null>(null);
  const [consumerId, setConsumerId] = useState('');
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [billLoaded, setBillLoaded] = useState(false);
  const [addPayeeToggle, setAddPayeeToggle] = useState(false);
  const [payeeName, setPayeeName] = useState('');
  const [comments, setComments] = useState('');
  const [fromAccount, setFromAccount] = useState(ACCOUNTS[0]);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [authMethod, setAuthMethod] = useState<AuthMethod>('mpin');
  const [mpin, setMpin] = useState(['', '', '', '']);
  const [authPhase, setAuthPhase] = useState<'idle' | 'scanning' | 'verified'>('idle');
  const [nickname, setNickname] = useState('');
  // Mock fetched details
  const fetchedBill = {
    consumerName: 'Muhammad Sohail Shawani',
    consumerNumber: consumerId || '03223466549',
    amount: 3448,
    dueDate: '14 May 2026',
    daysLeft: 7
  };
  // Sort payees by days left ascending (most urgent first)
  const sortedPayees = [...savedPayees].sort((a, b) => a.daysLeft - b.daysLeft);
  const homePayees = sortedPayees.slice(0, 3);
  const filteredBillers = ALL_BILLERS.filter(b => b.category === selectedCategory && b.name.toLowerCase().includes(billerSearch.toLowerCase()));
  // Note: bill detail fetching is handled in handleConsumerNext to avoid
  // effect-cleanup race conditions that prevented the loader from completing.
  // Auth effect
  useEffect(() => {
    if (step === 'auth' && authPhase === 'scanning' && (authMethod === 'thumb' || authMethod === 'face')) {
      const timer = setTimeout(() => {
        setAuthPhase('verified');
        setTimeout(() => {
          setStep('processing');
          setTimeout(() => setStep('success'), 1800);
        }, 1000);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [step, authPhase, authMethod]);
  // Handlers
  const handleResetFlow = () => {
    setStep('home');
    setSelectedBiller(null);
    setSelectedPayee(null);
    setConsumerId('');
    setBillLoaded(false);
    setLoadingDetails(false);
    setAddPayeeToggle(false);
    setPayeeName('');
    setComments('');
    setBillerSearch('');
    setMpin(['', '', '', '']);
    setAuthPhase('idle');
    setNickname('');
  };
  const handleCategoryClick = (catId: string) => {
    setSelectedCategory(catId);
    setStep('biller_list');
  };
  const handleBillerSelect = (biller: Biller) => {
    setSelectedBiller(biller);
    setStep('enter_consumer');
  };
  const handlePayeeClick = (payee: SavedPayee) => {
    setSelectedPayee(payee);
    setSelectedBiller({
      id: payee.id,
      name: payee.company,
      email: payee.email,
      category: payee.category,
      iconBg: payee.iconBg
    });
    setConsumerId(payee.consumerNumber);
    setBillLoaded(true);
    setPayeeName(payee.consumerName);
    setNickname(payee.consumerName);
    setStep('bill_details'); // Navigate to Payment Details (screen 2)
  };
  const handleConsumerNext = () => {
    if (consumerId.length < 8) return;
    // Inline fetch on the same enter_consumer screen — no step change.
    setBillLoaded(false);
    setLoadingDetails(true);
    const fetchedName = 'Muhammad Sohail Shawani';
    setTimeout(() => {
      setLoadingDetails(false);
      setBillLoaded(true);
      setPayeeName(fetchedName);
    }, 1800);
  };
  const saveCurrentPayee = () => {
    if (!selectedBiller) return;
    const newPayee: SavedPayee = {
      id: `p${Date.now()}`,
      company: selectedBiller.name,
      consumerName: payeeName || fetchedBill.consumerName,
      consumerNumber: consumerId,
      amount: fetchedBill.amount,
      daysLeft: fetchedBill.daysLeft,
      category: selectedBiller.category,
      iconBg: selectedBiller.iconBg,
      icon: getCategoryIcon(selectedBiller.category),
      email: selectedBiller.email
    };
    setSavedPayees(prev => [...prev, newPayee]);
    if (payeeName && payeeName !== fetchedBill.consumerName) {
      setNickname(payeeName);
    }
  };
  const handlePayAction = () => {
    if (addPayeeToggle) {
      saveCurrentPayee();
    }
    setStep('bill_details');
  };
  const handleSaveAction = () => {
    if (!addPayeeToggle) {
      setAddPayeeToggle(true);
      if (!payeeName) {
        setPayeeName(fetchedBill.consumerName);
      }
    } else {
      saveCurrentPayee();
      handleResetFlow();
      if (onNavigateHome) {
        onNavigateHome();
      } else {
        setStep('home');
      }
    }
  };
  const handleJustPayOnly = () => {
    // Pay without saving the payee.
    setStep('review');
  };
  const handleJustPay = () => {
    if (addPayeeToggle) {
      // Save payee
      const newPayee: SavedPayee = {
        id: `p${Date.now()}`,
        company: selectedBiller!.name,
        consumerName: payeeName,
        consumerNumber: consumerId,
        amount: fetchedBill.amount,
        daysLeft: fetchedBill.daysLeft,
        category: selectedBiller!.category,
        iconBg: selectedBiller!.iconBg,
        icon: getCategoryIcon(selectedBiller!.category),
        email: selectedBiller!.email
      };
      setSavedPayees(prev => [...prev, newPayee]);
    }
    setStep('review');
  };
  const handleSavePayee = () => {
    const newPayee: SavedPayee = {
      id: `p${Date.now()}`,
      company: selectedBiller!.name,
      consumerName: payeeName || fetchedBill.consumerName,
      consumerNumber: consumerId,
      amount: fetchedBill.amount,
      daysLeft: fetchedBill.daysLeft,
      category: selectedBiller!.category,
      iconBg: selectedBiller!.iconBg,
      icon: getCategoryIcon(selectedBiller!.category),
      email: selectedBiller!.email
    };
    setSavedPayees(prev => [...prev, newPayee]);
    // Reset transient fields but redirect to the Saved Payees screen per spec 4.3
    setSelectedBiller(null);
    setSelectedPayee(null);
    setConsumerId('');
    setBillLoaded(false);
    setLoadingDetails(false);
    setAddPayeeToggle(false);
    setPayeeName('');
    setComments('');
    setBillerSearch('');
    setMpin(['', '', '', '']);
    setAuthPhase('idle');
    setStep('view_all_payees');
  };
  const handleMpinDigit = (digit: string) => {
    const idx = mpin.findIndex(d => d === '');
    if (idx === -1 || !digit) return;
    const newMpin = [...mpin];
    newMpin[idx] = digit;
    setMpin(newMpin);
    if (idx === 3) {
      setTimeout(() => {
        setStep('processing');
        setTimeout(() => setStep('success'), 1800);
      }, 400);
    }
  };
  const handleMpinDelete = () => {
    const lastFilled = [...mpin].reverse().findIndex(d => d !== '');
    if (lastFilled === -1) return;
    const idx = 3 - lastFilled;
    const newMpin = [...mpin];
    newMpin[idx] = '';
    setMpin(newMpin);
  };
  const getCategoryIcon = (cat: string): React.ElementType => {
    const found = CATEGORIES.find(c => c.id === cat);
    return found?.icon || ZapIcon;
  };
  // Theme styles
  const bgStyle = isPlatinum ? 'bg-[#1C1C1E]' : isDark ? 'bg-[#1A0E08]' : 'bg-page-warm';
  const cardBg = isPlatinum ? 'bg-[#2A2A2C]' : isDark ? 'bg-[#2D1810]' : 'bg-white';
  const inputBg = isPlatinum ? 'bg-[#3A3A3C]' : isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]';
  const textPri = isPlatinum || isDark ? 'text-white' : 'text-[#303030]';
  const textSec = isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#B8A89E]' : 'text-[#818285]';
  const borderClr = isPlatinum ? 'border-[rgba(160,160,165,0.15)]' : isDark ? 'border-[rgba(243,112,36,0.1)]' : 'border-[#F0E8E3]';
  // ============== HOME STEP ==============
  if (step === 'home') {
    return <motion.div initial="hidden" animate="show" variants={stagger} className={`w-full min-h-screen pb-24 ${bgStyle}`} data-id="element-1540">
        <motion.div variants={fadeUp} className="px-6 pt-6 pb-2 flex items-center justify-between" data-id="element-1541">
          <button onClick={onBack} className={`p-2 -ml-2 rounded-full transition-colors ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} aria-label="Back" data-id="element-1542">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-1543" />
          </button>
          <h1 className={`text-lg font-bold ${textPri}`} data-id="element-1544">Bill Payments</h1>
          <button onClick={onNavigateHome} className="w-10 h-10 rounded-full bg-[#F37024] flex items-center justify-center text-white hover:bg-[#D95A1A] transition-colors shadow-sm" aria-label="Home" data-id="element-1545">
            <HomeIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-1546" />
          </button>
        </motion.div>
        <motion.div variants={fadeUp} className="px-6 pb-4" data-id="element-1547">
          <p className={`text-sm ${textSec}`} data-id="element-1548">
            Pay bills and manage your payees
          </p>
        </motion.div>

        <div className="px-6 space-y-6" data-id="element-1549">
          {/* Search Bar */}
          <motion.div variants={fadeUp} data-id="element-1550">
            <div className={`flex items-center gap-3 rounded-2xl px-4 py-3.5 ${inputBg} border ${borderClr}`} data-id="element-1551">
              <SearchIcon className={`w-5 h-5 ${textSec}`} strokeWidth={2} data-id="element-1552" />
              <input type="text" value={homeSearch} onChange={e => setHomeSearch(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && homeSearch.trim()) {
                setBillerSearch(homeSearch.trim());
                setStep('biller_list');
              }
            }} placeholder="Search billers, bills or payees" className={`flex-1 outline-none text-sm bg-transparent ${textPri} placeholder:opacity-60`} data-id="element-1553" />
              {homeSearch && <button onClick={() => {
              setBillerSearch(homeSearch.trim());
              setStep('biller_list');
            }} className="text-xs font-bold text-[#F37024] uppercase tracking-wider" data-id="element-1554">
                  Go
                </button>}
            </div>
          </motion.div>

          {/* Categories */}
          <motion.div variants={fadeUp} data-id="element-1555">
            <h3 className={`text-[13px] font-bold tracking-wider uppercase mb-4 ${isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#F37024]' : 'text-[#661806]'}`} data-id="element-1556">
              Categories
            </h3>
            <div className="grid grid-cols-3 gap-3" data-id="element-1557">
              {CATEGORIES.slice(0, 6).map(cat => {
              const Icon = cat.icon;
              return <button key={cat.id} onClick={() => handleCategoryClick(cat.id)} className={`rounded-2xl p-4 flex flex-col items-center gap-3 shadow-sm hover:shadow-md transition-all border ${cardBg} ${borderClr} active:scale-95`} data-id="element-1558">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${inputBg}`} data-id="element-1559">
                      <Icon className="w-5 h-5 text-[#F37024]" strokeWidth={2} data-id="element-1560" />
                    </div>
                    <span className={`text-[11px] font-medium text-center leading-tight ${textPri}`} data-id="element-1561">
                      {cat.label}
                    </span>
                  </button>;
            })}
            </div>
          </motion.div>

          {/* Saved Payees */}
          <motion.div variants={fadeUp} data-id="element-1562">
            <div className="flex items-center justify-between mb-4" data-id="element-1563">
              <h3 className={`text-[13px] font-bold tracking-wider uppercase ${isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#F37024]' : 'text-[#661806]'}`} data-id="element-1564">
                Saved Payees
              </h3>
              <button onClick={() => setStep('view_all_payees')} className="text-xs font-bold text-[#22C55E] uppercase tracking-wider flex items-center gap-1 hover:opacity-80 transition-opacity" data-id="element-1565">
                View All
                <ChevronRightIcon className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-1566" />
              </button>
            </div>

            {/* Payees List */}
            <div className="space-y-3" data-id="element-1567">
              {homePayees.length === 0 ? <div className={`text-center py-12 rounded-2xl ${cardBg} border ${borderClr}`} data-id="element-1568">
                  <FileTextIcon className={`w-10 h-10 mx-auto mb-2 ${textSec}`} data-id="element-1569" />
                  <p className={`text-sm ${textSec}`} data-id="element-1570">No saved payees yet</p>
                </div> : homePayees.map(payee => {
              const Icon = payee.icon;
              const isUrgent = payee.daysLeft <= 3;
              return <button key={payee.id} onClick={() => handlePayeeClick(payee)} className={`w-full rounded-2xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-all border ${cardBg} ${borderClr} active:scale-[0.98]`} data-id="element-1571">
                      <div className="relative shrink-0" data-id="element-1572">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{
                    backgroundColor: payee.iconBg
                  }} data-id="element-1573">
                          <Icon className="w-6 h-6 text-white" strokeWidth={2} data-id="element-1574" />
                        </div>
                        {isUrgent && <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#FBBF24] flex items-center justify-center border-2 border-white" data-id="element-1575">
                            <CheckCircle2Icon className="w-3 h-3 text-white" data-id="element-1576" />
                          </div>}
                      </div>
                      <div className="flex-1 text-left min-w-0" data-id="element-1577">
                        <h4 className={`text-[15px] font-bold truncate ${textPri}`} data-id="element-1578">
                          {payee.company}
                        </h4>
                        <p className={`text-[12px] truncate ${textSec}`} data-id="element-1579">
                          {payee.consumerName}
                        </p>
                        <p className={`text-[11px] ${textSec}`} data-id="element-1580">
                          {payee.consumerNumber}
                        </p>
                      </div>
                      <div className="text-right shrink-0" data-id="element-1581">
                        <p className={`text-[15px] font-bold ${textPri}`} data-id="element-1582">
                          Rs. {payee.amount.toLocaleString()}
                        </p>
                        <p className={`text-[11px] font-medium ${isUrgent ? 'text-[#EF4444]' : 'text-[#22C55E]'}`} data-id="element-1583">
                          {payee.daysLeft} days left
                        </p>
                      </div>
                      <ChevronRightIcon className={`w-5 h-5 ${isUrgent ? 'text-[#EF4444]' : 'text-[#22C55E]'} shrink-0`} data-id="element-1584" />
                    </button>;
            })}
            </div>

            
          </motion.div>
        </div>
      </motion.div>;
  }
  // ============== VIEW ALL PAYEES STEP ==============
  if (step === 'view_all_payees') {
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto`} data-id="element-1585">
        <div className="px-6 pt-6 pb-3 flex items-center gap-3" data-id="element-1586">
          <button onClick={() => setStep('home')} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-1587">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} data-id="element-1588" />
          </button>
          <h2 className={`text-lg font-bold flex-1 ${textPri}`} data-id="element-1589">
            All Saved Payees
          </h2>
        </div>

        <div className="px-6 mt-2 mb-4" data-id="element-1590">
          <p className={`text-sm ${textSec}`} data-id="element-1591">
            {sortedPayees.length}{' '}
            {sortedPayees.length === 1 ? 'payee' : 'payees'} • Sorted by due
            date
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-8" data-id="element-1592">
          <div className="space-y-3" data-id="element-1593">
            {sortedPayees.length === 0 ? <div className={`text-center py-12 rounded-2xl ${cardBg} border ${borderClr}`} data-id="element-1594">
                <FileTextIcon className={`w-10 h-10 mx-auto mb-2 ${textSec}`} data-id="element-1595" />
                <p className={`text-sm ${textSec}`} data-id="element-1596">No saved payees yet</p>
              </div> : sortedPayees.map(payee => {
            const Icon = payee.icon;
            const isUrgent = payee.daysLeft <= 3;
            return <button key={payee.id} onClick={() => handlePayeeClick(payee)} className={`w-full rounded-2xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-all border ${cardBg} ${borderClr} active:scale-[0.98]`} data-id="element-1597">
                    <div className="relative shrink-0" data-id="element-1598">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{
                  backgroundColor: payee.iconBg
                }} data-id="element-1599">
                        <Icon className="w-6 h-6 text-white" strokeWidth={2} data-id="element-1600" />
                      </div>
                      {isUrgent && <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#FBBF24] flex items-center justify-center border-2 border-white" data-id="element-1601">
                          <CheckCircle2Icon className="w-3 h-3 text-white" data-id="element-1602" />
                        </div>}
                    </div>
                    <div className="flex-1 text-left min-w-0" data-id="element-1603">
                      <h4 className={`text-[15px] font-bold truncate ${textPri}`} data-id="element-1604">
                        {payee.company}
                      </h4>
                      <p className={`text-[12px] truncate ${textSec}`} data-id="element-1605">
                        {payee.consumerName}
                      </p>
                      <p className={`text-[11px] ${textSec}`} data-id="element-1606">
                        {payee.consumerNumber}
                      </p>
                    </div>
                    <div className="text-right shrink-0" data-id="element-1607">
                      <p className={`text-[15px] font-bold ${textPri}`} data-id="element-1608">
                        Rs. {payee.amount.toLocaleString()}
                      </p>
                      <p className={`text-[11px] font-medium ${isUrgent ? 'text-[#EF4444]' : 'text-[#22C55E]'}`} data-id="element-1609">
                        {payee.daysLeft} days left
                      </p>
                    </div>
                    <ChevronRightIcon className={`w-5 h-5 ${isUrgent ? 'text-[#EF4444]' : 'text-[#22C55E]'} shrink-0`} data-id="element-1610" />
                  </button>;
          })}
          </div>
        </div>
      </motion.div>;
  }
  // ============== BILLER LIST STEP ==============
  if (step === 'biller_list') {
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto`} data-id="element-1611">
        {/* Header */}
        <div className="px-6 pt-6 pb-3 flex items-center gap-3" data-id="element-1612">
          <button onClick={handleResetFlow} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-1613">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} data-id="element-1614" />
          </button>
          <div className={`flex-1 rounded-full px-4 py-3 flex items-center gap-2 ${inputBg}`} data-id="element-1615">
            <SearchIcon className={`w-4 h-4 ${textSec}`} data-id="element-1616" />
            <input type="text" placeholder="Search biller" value={billerSearch} onChange={e => setBillerSearch(e.target.value)} className={`flex-1 outline-none text-sm bg-transparent ${textPri} placeholder:opacity-60`} data-id="element-1617" />
          </div>
        </div>

        {/* Title */}
        <div className="px-6 mt-2 mb-4" data-id="element-1618">
          <h1 className={`text-3xl font-bold ${textPri}`} data-id="element-1619">Select Biller</h1>
        </div>

        {/* Category Tabs */}
        <div className="px-6 mb-4 overflow-x-auto" data-id="element-1620">
          <div className="flex gap-2 pb-2" data-id="element-1621">
            {CATEGORIES.map(cat => <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all ${selectedCategory === cat.id ? 'bg-[#F37024] text-white shadow-md' : `${inputBg} ${textSec}`}`} data-id="element-1622">
                {cat.label}
              </button>)}
          </div>
        </div>

        {/* Biller List */}
        <div className="flex-1 overflow-y-auto px-6 pb-8" data-id="element-1623">
          {filteredBillers.length === 0 ? <div className={`text-center py-12 ${textSec}`} data-id="element-1624">
              No billers found
            </div> : <div className="space-y-1" data-id="element-1625">
              {filteredBillers.map((biller, idx) => <button key={biller.id} onClick={() => handleBillerSelect(biller)} className={`w-full py-4 flex items-center gap-4 ${idx > 0 ? `border-t ${borderClr}` : ''} active:opacity-70 transition-opacity`} data-id="element-1626">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 border ${borderClr} ${cardBg}`} data-id="element-1627">
                    <span className="font-bold text-sm" style={{
                color: biller.iconBg
              }} data-id="element-1628">
                      {biller.name.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 text-left min-w-0" data-id="element-1629">
                    <h4 className={`text-[15px] font-bold ${textPri}`} data-id="element-1630">
                      {biller.name}
                    </h4>
                    <p className={`text-xs ${textSec}`} data-id="element-1631">{biller.email}</p>
                  </div>
                </button>)}
            </div>}
        </div>
      </motion.div>;
  }
  // ============== ENTER CONSUMER ID + FETCH + ACTIONS (single screen) ==============
  if (step === 'enter_consumer') {
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto overflow-y-auto`} data-id="element-1632">
        <div className="px-6 pt-6 pb-3 flex items-center gap-3" data-id="element-1633">
          <button onClick={() => {
          setBillLoaded(false);
          setLoadingDetails(false);
          setConsumerId('');
          setStep('biller_list');
        }} className="p-2 -ml-2" data-id="element-1634">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} data-id="element-1635" />
          </button>
          <h2 className={`text-lg font-bold flex-1 ${textPri}`} data-id="element-1636">
            {billLoaded ? 'Bill Details' : 'Enter Bill ID'}
          </h2>
        </div>

        <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-1637">
          {/* Biller Header */}
          <div className="flex flex-col items-center mb-6 mt-2" data-id="element-1638">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4 shadow-lg" style={{
            backgroundColor: selectedBiller?.iconBg
          }} data-id="element-1639">
              <span className="text-3xl font-bold text-white" data-id="element-1640">
                {selectedBiller?.name.charAt(0)}
              </span>
            </div>
            <h1 className={`text-2xl font-bold ${textPri}`} data-id="element-1641">
              {selectedBiller?.name}
            </h1>
            <p className={`text-sm ${textSec}`} data-id="element-1642">{selectedBiller?.email}</p>
          </div>

          {/* Consumer/Bill ID Input */}
          <div className="relative mb-3" data-id="element-1643">
            <div className={`relative rounded-3xl px-6 py-5 border-2 ${consumerId ? 'border-[#F37024]' : borderClr} ${inputBg} flex flex-col justify-center min-h-[80px] transition-colors`} data-id="element-1644">
              <label className={`text-xs font-bold uppercase tracking-wider transition-all duration-200 ${consumerId ? 'opacity-100 text-[#F37024] mb-1' : 'opacity-0 h-0 overflow-hidden'}`} data-id="element-1645">
                Consumer / Bill ID
              </label>
              <input type="text" value={consumerId} disabled={loadingDetails || billLoaded} onChange={e => {
              setConsumerId(e.target.value.replace(/[^\d]/g, ''));
              if (billLoaded) setBillLoaded(false);
            }} placeholder="Enter Consumer / Bill ID" className={`w-full outline-none text-xl tracking-wider bg-transparent font-medium ${textPri} placeholder:opacity-50 placeholder:text-base placeholder:font-normal disabled:opacity-70`} data-id="element-1646" />
            </div>
          </div>
          <p className={`text-xs ${textSec} text-center mb-4`} data-id="element-1647">
            You can find this on your physical bill
          </p>

          {/* Fetch Button — only shown before fetch completes */}
          {!billLoaded && !loadingDetails && <button onClick={handleConsumerNext} disabled={consumerId.length < 8} className="w-full bg-gradient-to-r from-[#22C55E] to-[#16A34A] text-white py-4 rounded-full font-bold text-[15px] tracking-wide shadow-[0_8px_20px_rgba(34,197,94,0.3)] active:scale-[0.98] transition-all disabled:opacity-50" data-id="element-1648">
              FETCH BILL DETAILS
            </button>}

          {/* Inline Loader */}
          <AnimatePresence data-id="element-1649">
            {loadingDetails && <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} className="flex flex-col items-center justify-center py-8" data-id="element-1650">
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 1.2,
              repeat: Infinity,
              ease: 'linear'
            }} className="w-12 h-12 rounded-full border-4 border-[#F37024]/20 border-t-[#F37024] mb-4" data-id="element-1651" />
                <p className={`text-sm font-bold ${textPri}`} data-id="element-1652">
                  Fetching bill details...
                </p>
              </motion.div>}
          </AnimatePresence>

          {/* Inline Fetched Details + Actions */}
          <AnimatePresence data-id="element-1653">
            {billLoaded && !loadingDetails && <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} className="mt-2" data-id="element-1654">
                <div className={`rounded-3xl p-6 ${cardBg} shadow-lg border ${borderClr}`} data-id="element-1655">
                  <div className="flex items-center gap-4 mb-4 pb-4 border-b border-dashed" style={{
                borderColor: isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E0D5CC'
              }} data-id="element-1656">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{
                  backgroundColor: selectedBiller?.iconBg
                }} data-id="element-1657">
                      <span className="text-lg font-bold text-white" data-id="element-1658">
                        {selectedBiller?.name.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1659">
                      <p className={`text-[15px] font-bold truncate ${textPri}`} data-id="element-1660">
                        {selectedBiller?.name}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-1661">
                    <div className="flex justify-between items-center" data-id="element-1662">
                      <span className={`text-sm ${textSec}`} data-id="element-1663">
                        Consumer Name
                      </span>
                      <span className={`text-sm font-bold ${textPri}`} data-id="element-1664">
                        {fetchedBill.consumerName}
                      </span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-1665">
                      <span className={`text-sm ${textSec}`} data-id="element-1666">Consumer ID</span>
                      <span className={`text-sm font-bold ${textPri}`} data-id="element-1667">
                        {consumerId || fetchedBill.consumerNumber}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Add Payee Toggle */}
                <div className={`mt-6 p-4 rounded-2xl flex items-center justify-between ${cardBg} border ${borderClr}`} data-id="element-1668">
                  <div className="flex-1" data-id="element-1669">
                    <h4 className={`font-bold text-sm ${textPri}`} data-id="element-1670">
                      Save as Payee
                    </h4>
                    <p className={`text-xs ${textSec}`} data-id="element-1671">
                      Quick access for future payments
                    </p>
                  </div>
                  <button onClick={() => setAddPayeeToggle(!addPayeeToggle)} className={`w-12 h-7 rounded-full transition-colors ${addPayeeToggle ? 'bg-[#22C55E]' : isDark || isPlatinum ? 'bg-[#48484A]' : 'bg-[#E5E5EA]'} relative`} data-id="element-1672">
                    <motion.div animate={{
                  x: addPayeeToggle ? 22 : 2
                }} transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }} className="absolute top-1 left-0 w-5 h-5 bg-white rounded-full shadow-md" data-id="element-1673" />
                  </button>
                </div>

                {/* Editable Payee Name (when toggle ON) */}
                <AnimatePresence data-id="element-1674">
                  {addPayeeToggle && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="mt-3 overflow-hidden" data-id="element-1675">
                      <div className={`rounded-2xl px-5 py-3 ${inputBg} border ${borderClr}`} data-id="element-1676">
                        <label className={`text-[10px] font-bold uppercase tracking-wider ${isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#C4B5AA]' : 'text-[#5A5B5E]'}`} data-id="element-1677">
                          Payee Name
                        </label>
                        <input type="text" value={payeeName} onChange={e => setPayeeName(e.target.value)} className={`w-full outline-none text-base bg-transparent font-medium ${textPri}`} data-id="element-1678" />
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* Action Buttons */}
                <div className="mt-6 flex flex-col gap-3" data-id="element-1679">
                  <button onClick={handleSaveAction} className="w-full border-2 border-[#22C55E] text-[#22C55E] py-4 rounded-full font-bold text-[15px] tracking-wide active:scale-[0.98] transition-all" data-id="element-1680">
                    SAVE
                  </button>
                  <button onClick={handlePayAction} className="w-full bg-gradient-to-r from-[#22C55E] to-[#16A34A] text-white py-4 rounded-full font-bold text-[15px] tracking-wide shadow-[0_8px_20px_rgba(34,197,94,0.3)] active:scale-[0.98] transition-all" data-id="element-1681">
                    PAY
                  </button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </motion.div>;
  }
  // ============== BILL SCREEN (Payment Details) ==============
  if (step === 'bill_details') {
    const isOverdue = fetchedBill.daysLeft < 0;
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto overflow-y-auto`} data-id="element-1682">
        <div className="px-6 pt-6 pb-3 flex items-center gap-3" data-id="element-1683">
          <button onClick={() => {
          setShowAccountDropdown(false);
          setStep('enter_consumer');
        }} className="p-2 -ml-2" aria-label="Back" data-id="element-1684">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} data-id="element-1685" />
          </button>
          <h2 className={`text-lg font-bold flex-1 ${textPri}`} data-id="element-1686">
            Payment Details
          </h2>
        </div>

        <div className="px-6 flex-1 flex flex-col pb-8 space-y-5" data-id="element-1687">
          {/* From Account Card with dropdown */}
          <div data-id="element-1688">
            <p className={`text-[11px] font-bold uppercase tracking-widest mb-2 ${textSec}`} data-id="element-1689">
              From Account
            </p>
            <div className={`rounded-2xl border ${borderClr} ${cardBg} overflow-hidden`} data-id="element-1690">
              <button onClick={() => setShowAccountDropdown(!showAccountDropdown)} className="w-full p-4 flex items-center justify-between text-left active:opacity-70 transition-opacity" data-id="element-1691">
                <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-1692">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-[#22C55E]/10`} data-id="element-1693">
                    <BuildingIcon className="w-5 h-5 text-[#22C55E]" strokeWidth={2} data-id="element-1694" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-1695">
                    <p className={`text-[15px] font-bold truncate ${textPri}`} data-id="element-1696">
                      {fromAccount.label}
                    </p>
                    <p className={`text-xs truncate ${textSec}`} data-id="element-1697">
                      {fromAccount.number} • {fromAccount.balance}
                    </p>
                  </div>
                </div>
                <ChevronRightIcon className={`w-5 h-5 ${textSec} transition-transform ${showAccountDropdown ? 'rotate-90' : ''}`} strokeWidth={2} data-id="element-1698" />
              </button>

              <AnimatePresence data-id="element-1699">
                {showAccountDropdown && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="overflow-hidden" data-id="element-1700">
                    <div className={`px-3 pb-3 space-y-2 border-t ${borderClr} pt-3`} data-id="element-1701">
                      {ACCOUNTS.map(acc => <button key={acc.id} onClick={() => {
                    setFromAccount(acc);
                    setShowAccountDropdown(false);
                  }} className={`w-full p-3 rounded-xl text-left flex items-center justify-between transition-colors ${fromAccount.id === acc.id ? 'bg-[#22C55E]/10 border border-[#22C55E]/30' : `${inputBg} border ${borderClr}`}`} data-id="element-1702">
                          <div className="min-w-0" data-id="element-1703">
                            <p className={`text-sm font-bold ${textPri}`} data-id="element-1704">
                              {acc.label}
                            </p>
                            <p className={`text-xs ${textSec}`} data-id="element-1705">
                              {acc.number} • {acc.balance}
                            </p>
                          </div>
                          {fromAccount.id === acc.id && <CheckCircle2Icon className="w-5 h-5 text-[#22C55E] shrink-0" data-id="element-1706" />}
                        </button>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Biller Details Card */}
          <div data-id="element-1707">
            <p className={`text-[11px] font-bold uppercase tracking-widest mb-2 ${textSec}`} data-id="element-1708">
              Biller Details
            </p>
            <div className={`rounded-2xl border ${borderClr} ${cardBg} overflow-hidden`} data-id="element-1709">
              {/* Header */}
              <div className={`p-4 flex items-center gap-3 border-b ${borderClr}`} data-id="element-1710">
                <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{
                backgroundColor: selectedBiller?.iconBg
              }} data-id="element-1711">
                  <span className="text-lg font-bold text-white" data-id="element-1712">
                    {selectedBiller?.name.charAt(0)}
                  </span>
                </div>
                <div className="min-w-0 flex-1" data-id="element-1713">
                  <p className={`text-[15px] font-bold truncate ${textPri}`} data-id="element-1714">
                    {selectedBiller?.name}
                  </p>
                  <p className={`text-xs truncate ${textSec}`} data-id="element-1715">
                    {selectedBiller?.email}
                  </p>
                </div>
              </div>
              {/* Rows */}
              <div className="p-4 space-y-3" data-id="element-1716">
                <div className="flex justify-between items-center" data-id="element-1717">
                  <span className={`text-sm ${textSec}`} data-id="element-1718">Bill Company</span>
                  <span className={`text-sm font-bold ${textPri}`} data-id="element-1719">
                    {selectedBiller?.name}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1720">
                  <span className={`text-sm ${textSec}`} data-id="element-1721">Bill Amount</span>
                  <span className={`text-sm font-bold ${textPri}`} data-id="element-1722">
                    Rs. {fetchedBill.amount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1723">
                  <span className={`text-sm ${textSec}`} data-id="element-1724">Consumer Name</span>
                  <span className={`text-sm font-bold ${textPri}`} data-id="element-1725">
                    {payeeName || fetchedBill.consumerName}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1726">
                  <span className={`text-sm ${textSec}`} data-id="element-1727">Consumer ID</span>
                  <span className={`text-sm font-bold ${textPri}`} data-id="element-1728">
                    {'03223556978'}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1729">
                  <span className={`text-sm ${textSec}`} data-id="element-1730">Due Date</span>
                  <span className={`text-sm font-bold ${isOverdue ? 'text-[#EF4444]' : textPri}`} data-id="element-1731">
                    {fetchedBill.dueDate}
                    {isOverdue && ' (Overdue)'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Comments */}
          <div data-id="element-1732">
            <p className={`text-[11px] font-bold uppercase tracking-widest mb-2 ${textSec}`} data-id="element-1733">
              Comments (Optional)
            </p>
            <textarea value={comments} onChange={e => setComments(e.target.value)} placeholder="Add a note for this payment..." rows={3} className={`w-full rounded-2xl px-4 py-3 text-sm outline-none resize-none ${inputBg} ${textPri} placeholder:opacity-50 border ${borderClr}`} data-id="element-1734" />
          </div>

          {/* Next Button */}
          <div className="mt-auto pt-4" data-id="element-1735">
            <button onClick={() => {
            setShowAccountDropdown(false);
            setStep('review');
          }} className="w-full bg-[#F37024] text-white py-4 rounded-full font-bold text-[15px] tracking-wide shadow-[0_8px_20px_rgba(34,197,94,0.3)] active:scale-[0.98] transition-all" data-id="element-1736">
              NEXT
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // ============== REVIEW STEP (read-only) ==============
  if (step === 'review') {
    const isOverdue = fetchedBill.daysLeft < 0;
    const overdueSurcharge = isOverdue ? Math.abs(fetchedBill.daysLeft) * OVERDUE_SURCHARGE_PER_DAY : 0;
    const totalAmount = fetchedBill.amount + TRANSACTION_FEE + overdueSurcharge;
    const showNickname = !!nickname && nickname !== fetchedBill.consumerName;
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto overflow-y-auto`} data-id="element-1737">
        <div className="px-6 pt-6 pb-3 flex items-center gap-3" data-id="element-1738">
          <button onClick={() => setStep('bill_details')} className="p-2 -ml-2" aria-label="Back" data-id="element-1739">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} data-id="element-1740" />
          </button>
          <h2 className={`text-lg font-bold flex-1 ${textPri}`} data-id="element-1741">
            Review Payment
          </h2>
        </div>

        <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-1742">
          {/* Ticket */}
          <div className="relative mt-4" data-id="element-1743">
            <div className={`rounded-3xl ${cardBg} shadow-xl overflow-hidden border ${borderClr}`} data-id="element-1744">
              {/* Top: From Account + Amount (read-only) */}
              <div className="bg-gradient-to-br from-[#F37024] to-[#FB8E4F] p-6 text-white" data-id="element-1745">
                <p className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1" data-id="element-1746">
                  From Account
                </p>
                <h3 className="text-lg font-bold" data-id="element-1747">{fromAccount.label}</h3>
                <p className="text-xs opacity-80" data-id="element-1748">
                  {fromAccount.number} • {fromAccount.balance}
                </p>

                <div className="mt-4 pt-4 border-t border-white/20" data-id="element-1749">
                  <p className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1" data-id="element-1750">
                    Amount
                  </p>
                  <p className="text-3xl font-bold" data-id="element-1751">
                    Rs. {totalAmount.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Notch separator */}
              <div className="relative h-6" data-id="element-1752">
                <div className={`absolute inset-0 ${cardBg}`} data-id="element-1753" />
                <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full" style={{
                backgroundColor: isPlatinum ? '#1C1C1E' : isDark ? '#1A0E08' : '#FFF7F3'
              }} data-id="element-1754" />
                <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full" style={{
                backgroundColor: isPlatinum ? '#1C1C1E' : isDark ? '#1A0E08' : '#FFF7F3'
              }} data-id="element-1755" />
                <div className="absolute left-6 right-6 top-1/2 border-t-2 border-dashed" style={{
                borderColor: isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E0D5CC'
              }} data-id="element-1756" />
              </div>

              {/* Body */}
              <div className="p-6 pt-2 space-y-4" data-id="element-1757">
                {/* Pay To */}
                <div className="flex items-center gap-3 pb-4 border-b border-dashed" style={{
                borderColor: isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E0D5CC'
              }} data-id="element-1758">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{
                  backgroundColor: selectedBiller?.iconBg
                }} data-id="element-1759">
                    <span className="text-lg font-bold text-white" data-id="element-1760">
                      {selectedBiller?.name.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1761">
                    <p className={`text-[10px] font-bold uppercase tracking-widest ${textSec}`} data-id="element-1762">
                      Pay To
                    </p>
                    <p className={`text-[15px] font-bold truncate ${textPri}`} data-id="element-1763">
                      {selectedBiller?.name}
                    </p>
                    <p className={`text-xs truncate ${textSec}`} data-id="element-1764">
                      {selectedBiller?.email}
                    </p>
                  </div>
                </div>

                {/* Detail rows */}
                <div className="space-y-3" data-id="element-1765">
                  <div className="flex justify-between items-center" data-id="element-1766">
                    <span className={`text-sm ${textSec}`} data-id="element-1767">Bill Company</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1768">
                      {selectedBiller?.name}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1769">
                    <span className={`text-sm ${textSec}`} data-id="element-1770">Bill Amount</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1771">
                      Rs. {fetchedBill.amount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1772">
                    <span className={`text-sm ${textSec}`} data-id="element-1773">Consumer Name</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1774">
                      {fetchedBill.consumerName}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1775">
                    <span className={`text-sm ${textSec}`} data-id="element-1776">Consumer ID</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1777">
                      {consumerId}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1778">
                    <span className={`text-sm ${textSec}`} data-id="element-1779">Due Date</span>
                    <span className={`text-sm font-bold ${isOverdue ? 'text-[#EF4444]' : textPri}`} data-id="element-1780">
                      {fetchedBill.dueDate}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1781">
                    <span className={`text-sm ${textSec}`} data-id="element-1782">
                      Transaction Fee
                    </span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1783">
                      Rs. {TRANSACTION_FEE.toLocaleString()}
                    </span>
                  </div>

                  {/* Overdue surcharge (conditional) */}
                  {isOverdue && <div className="flex justify-between items-center bg-[#EF4444]/10 -mx-2 px-2 py-2 rounded-lg" data-id="element-1784">
                      <span className="text-sm font-bold text-[#EF4444]" data-id="element-1785">
                        Overdue Surcharge
                      </span>
                      <span className="text-sm font-bold text-[#EF4444]" data-id="element-1786">
                        Rs. {overdueSurcharge.toLocaleString()}
                      </span>
                    </div>}

                  {/* Total */}
                  <div className={`flex justify-between items-center pt-3 border-t ${borderClr}`} data-id="element-1787">
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1788">
                      Total Amount
                    </span>
                    <span className="text-base font-bold text-[#22C55E]" data-id="element-1789">
                      Rs. {totalAmount.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Nickname (conditional) */}
                {showNickname && <div className={`pt-4 border-t ${borderClr}`} data-id="element-1790">
                    <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${textSec}`} data-id="element-1791">
                      Nickname
                    </p>
                    <p className={`text-sm font-bold ${textPri}`} data-id="element-1792">{nickname}</p>
                  </div>}

                {/* Comments display (conditional, read-only) */}
                {comments.trim() && <div className={`pt-4 border-t ${borderClr}`} data-id="element-1793">
                    <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${textSec}`} data-id="element-1794">
                      Comments
                    </p>
                    <p className={`text-sm ${textPri} leading-relaxed`} data-id="element-1795">
                      {comments}
                    </p>
                  </div>}
              </div>
            </div>
          </div>

          <div className="mt-auto pt-6" data-id="element-1796">
            <button onClick={() => {
            setMpin(['', '', '', '']);
            setAuthPhase('idle');
            setAuthMethod('mpin');
            setStep('processing');
            setTimeout(() => setStep('success'), 1800);
          }} className="w-full bg-[#F37024] text-white py-4 rounded-full font-bold text-[15px] tracking-wide shadow-[0_8px_20px_rgba(34,197,94,0.3)] active:scale-[0.98] transition-all" data-id="element-1797">
              PAY BILL
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // ============== AUTH STEP ==============
  if (step === 'auth') {
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto overflow-y-auto`} data-id="element-1798">
        <div className="px-6 pt-6 pb-3 flex items-center gap-3" data-id="element-1799">
          <button onClick={() => setStep('review')} className="p-2 -ml-2" data-id="element-1800">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} data-id="element-1801" />
          </button>
          <h2 className={`text-lg font-bold flex-1 ${textPri}`} data-id="element-1802">
            {authPhase === 'scanning' ? 'Authenticating' : 'Enter MPIN'}
          </h2>
        </div>

        <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-1803">
          {authPhase === 'scanning' ? <div className="flex flex-col items-center justify-center h-full" data-id="element-1804">
              <div className="w-16 h-16 rounded-full border-4 border-[#F37024]/20 border-t-[#F37024] mb-4 animate-spin" style={{
            borderColor: isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E0D5CC'
          }} data-id="element-1805" />
              <p className={`text-sm font-bold ${textPri}`} data-id="element-1806">
                {authPhase === 'scanning' ? 'Authenticating...' : 'Enter MPIN'}
              </p>
            </div> : <div className="space-y-4" data-id="element-1807">
              <div className="flex flex-col items-center" data-id="element-1808">
                <p className={`text-sm ${textSec} mb-2`} data-id="element-1809">
                  Enter your 4-digit MPIN
                </p>
                <div className="flex gap-2" data-id="element-1810">
                  {mpin.map((digit, idx) => <input key={idx} type="text" value={digit} onChange={e => handleMpinDigit(e.target.value)} className={`w-12 h-12 rounded-2xl text-center font-bold text-lg ${textPri} border ${borderClr} outline-none`} maxLength={1} autoFocus={idx === 0} data-id="element-1811" />)}
                </div>
                <button onClick={handleMpinDelete} className="mt-4 w-12 h-12 rounded-2xl flex items-center justify-center text-[#6B7280] hover:bg-[#1F2937] transition-colors" data-id="element-1812">
                  <XIcon className="w-5 h-5" strokeWidth={2} data-id="element-1813" />
                </button>
              </div>

              <div className="flex flex-col items-center" data-id="element-1814">
                <p className={`text-sm ${textSec} mb-2`} data-id="element-1815">
                  {authMethod === 'thumb' ? 'Place your thumb' : 'Place your face'}
                </p>
                <div className={`w-32 h-32 rounded-full flex items-center justify-center border-2 ${borderClr} ${cardBg} ${authPhase === 'scanning' ? 'animate-pulse' : ''}`} data-id="element-1816">
                  {authPhase === 'scanning' ? <div className="w-24 h-24 rounded-full border-4 border-[#F37024]/20 border-t-[#F37024] animate-spin" style={{
                borderColor: isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E0D5CC'
              }} data-id="element-1817" /> : <div className="w-24 h-24 rounded-full border-4 border-[#F37024]/20 border-t-[#F37024]" style={{
                borderColor: isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E0D5CC'
              }} data-id="element-1818" />}
                </div>
              </div>
            </div>}
        </div>
      </motion.div>;
  }
  // ============== PROCESSING STEP ==============
  if (step === 'processing') {
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto overflow-y-auto`} data-id="element-1819">
        <div className="px-6 pt-6 pb-3 flex items-center gap-3" data-id="element-1820">
          <button onClick={() => setStep('review')} className="p-2 -ml-2" data-id="element-1821">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} data-id="element-1822" />
          </button>
          <h2 className={`text-lg font-bold flex-1 ${textPri}`} data-id="element-1823">
            Processing Payment
          </h2>
        </div>

        <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-1824">
          <div className="flex flex-col items-center justify-center h-full" data-id="element-1825">
            <div className="w-16 h-16 rounded-full border-4 border-[#F37024]/20 border-t-[#F37024] mb-4 animate-spin" style={{
            borderColor: isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E0D5CC'
          }} data-id="element-1826" />
            <p className={`text-sm font-bold ${textPri}`} data-id="element-1827">
              Processing payment...
            </p>
          </div>
        </div>
      </motion.div>;
  }
  // ============== RECEIPT STEP ==============
  if (step === 'success') {
    const isOverdue = fetchedBill.daysLeft < 0;
    const overdueSurcharge = isOverdue ? Math.abs(fetchedBill.daysLeft) * OVERDUE_SURCHARGE_PER_DAY : 0;
    const totalAmount = fetchedBill.amount + TRANSACTION_FEE + overdueSurcharge;
    const showNickname = !!nickname && nickname !== fetchedBill.consumerName;
    const txnId = `TXN${Date.now().toString().slice(-9)}`;
    const dateTime = new Date().toLocaleString('en-PK', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto overflow-y-auto`} data-id="element-1828">
        {/* Green success banner */}
        <div className="bg-gradient-to-br from-[#F37024] to-[#16A34A] px-6 pt-12 pb-12 relative" data-id="element-1829">
          <button onClick={handleResetFlow} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center" aria-label="Close" data-id="element-1830">
            <XIcon className="w-5 h-5 text-white" strokeWidth={2.5} data-id="element-1831" />
          </button>
          <div className="flex flex-col items-center text-white" data-id="element-1832">
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            damping: 12,
            stiffness: 200,
            delay: 0.15
          }} className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mb-4" data-id="element-1833">
              <CheckCircle2Icon className="w-12 h-12 text-white" strokeWidth={2} data-id="element-1834" />
            </motion.div>
            <p className="text-xs font-bold uppercase tracking-widest opacity-80" data-id="element-1835">
              Payment Successful
            </p>
            <p className="text-4xl font-bold mt-2" data-id="element-1836">
              Rs. {totalAmount.toLocaleString()}
            </p>
            <p className="text-sm opacity-80 mt-1" data-id="element-1837">
              Paid to {selectedBiller?.name}
            </p>
          </div>
        </div>

        {/* Wavy separator */}
        <svg viewBox="0 0 430 30" className="-mt-px" preserveAspectRatio="none" style={{
        height: 30
      }} data-id="element-1838">
          <path d="M0,0 C120,30 280,30 430,0 L430,30 L0,30 Z" fill={isPlatinum ? '#1C1C1E' : isDark ? '#1A0E08' : '#FFF7F3'} data-id="element-1839" />
        </svg>

        {/* Receipt body */}
        <div className="px-6 -mt-4 pb-8" data-id="element-1840">
          <div className={`rounded-3xl ${cardBg} shadow-xl border ${borderClr} overflow-hidden relative`} data-id="element-1841">
            {/* PAID watermark */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-[0.07] z-0" data-id="element-1842">
              <p className="text-[100px] font-black text-[#22C55E] tracking-tighter rotate-[-20deg]" data-id="element-1843">
                PAID
              </p>
            </div>

            <div className="p-6 relative z-10" data-id="element-1844">
              <h3 className={`text-xs font-bold uppercase tracking-widest mb-4 ${textSec}`} data-id="element-1845">
                Transaction Details
              </h3>
              <div className="space-y-3 mb-5" data-id="element-1846">
                <div className="flex justify-between" data-id="element-1847">
                  <span className={`text-sm ${textSec}`} data-id="element-1848">Transaction ID</span>
                  <span className={`text-sm font-bold ${textPri}`} data-id="element-1849">
                    {txnId}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-1850">
                  <span className={`text-sm ${textSec}`} data-id="element-1851">Date & Time</span>
                  <span className={`text-sm font-bold ${textPri}`} data-id="element-1852">
                    {dateTime}
                  </span>
                </div>
              </div>

              <div className={`pt-4 border-t border-dashed ${isPlatinum ? 'border-[#48484A]' : isDark ? 'border-[#3D2518]' : 'border-[#E0D5CC]'}`} data-id="element-1853">
                <h3 className={`text-xs font-bold uppercase tracking-widest mb-3 ${textSec}`} data-id="element-1854">
                  Bill Details
                </h3>
                <div className="space-y-3" data-id="element-1855">
                  <div className="flex justify-between" data-id="element-1856">
                    <span className={`text-sm ${textSec}`} data-id="element-1857">Bill Company</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1858">
                      {selectedBiller?.name}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1859">
                    <span className={`text-sm ${textSec}`} data-id="element-1860">Bill Amount</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1861">
                      Rs. {fetchedBill.amount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1862">
                    <span className={`text-sm ${textSec}`} data-id="element-1863">Consumer Name</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1864">
                      {fetchedBill.consumerName}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1865">
                    <span className={`text-sm ${textSec}`} data-id="element-1866">Consumer ID</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1867">
                      {consumerId}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1868">
                    <span className={`text-sm ${textSec}`} data-id="element-1869">Due Date</span>
                    <span className={`text-sm font-bold ${isOverdue ? 'text-[#EF4444]' : textPri}`} data-id="element-1870">
                      {fetchedBill.dueDate}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1871">
                    <span className={`text-sm ${textSec}`} data-id="element-1872">From Account</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1873">
                      {fromAccount.label} {fromAccount.number}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1874">
                    <span className={`text-sm ${textSec}`} data-id="element-1875">
                      Transaction Fee
                    </span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1876">
                      Rs. {TRANSACTION_FEE.toLocaleString()}
                    </span>
                  </div>
                  {isOverdue && <div className="flex justify-between" data-id="element-1877">
                      <span className="text-sm font-bold text-[#EF4444]" data-id="element-1878">
                        Overdue Surcharge
                      </span>
                      <span className="text-sm font-bold text-[#EF4444]" data-id="element-1879">
                        Rs. {overdueSurcharge.toLocaleString()}
                      </span>
                    </div>}
                  <div className={`flex justify-between pt-3 border-t ${borderClr}`} data-id="element-1880">
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1881">
                      Total Paid
                    </span>
                    <span className="text-base font-bold text-[#22C55E]" data-id="element-1882">
                      Rs. {totalAmount.toLocaleString()}
                    </span>
                  </div>
                  {showNickname && <div className="flex justify-between" data-id="element-1883">
                      <span className={`text-sm ${textSec}`} data-id="element-1884">Nickname</span>
                      <span className={`text-sm font-bold ${textPri}`} data-id="element-1885">
                        {nickname}
                      </span>
                    </div>}
                  {comments.trim() && <div className={`pt-3 border-t ${borderClr}`} data-id="element-1886">
                      <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${textSec}`} data-id="element-1887">
                        Comments
                      </p>
                      <p className={`text-sm ${textPri} leading-relaxed`} data-id="element-1888">
                        {comments}
                      </p>
                    </div>}
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6" data-id="element-1889">
            <button className="flex-1 py-4 rounded-full font-bold text-sm tracking-wide flex items-center justify-center gap-2 border-2 border-[#22C55E] text-[#22C55E] active:scale-[0.98] transition-all" data-id="element-1890">
              <ShareIcon className="w-4 h-4" data-id="element-1891" />
              SHARE
            </button>
            <button onClick={handleResetFlow} className="flex-1 bg-gradient-to-r from-[#22C55E] to-[#16A34A] text-white py-4 rounded-full font-bold text-sm tracking-wide active:scale-[0.98] transition-all" data-id="element-1892">
              DASHBOARD
            </button>
          </div>
        </div>
      </motion.div>;
  }
  return null;
}